/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { startOfMonth, startOfQuarter, startOfWeek, startOfYear, subMonths } from 'date-fns';
import { useCallback, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { type SessionStatus } from '../../lib/api/sessions.js';
import { type Timesheet, type TimesheetRange, type TimesheetRow } from '../../lib/api/timesheet.js';
import { cn } from '../../lib/utils/cn.js';
import { formatIst } from '../../lib/utils/datetime.js';
import { formatInr } from '../../lib/utils/money.js';
import { Badge, type BadgeTone } from '../ui/badge.js';
import { QueryState } from '../ui/query-state.js';

export const TIMESHEET_PERIODS = [
  'this_week',
  'this_month',
  'this_quarter',
  'half_year',
  'this_year',
  'all',
] as const;
export type TimesheetPeriod = (typeof TIMESHEET_PERIODS)[number];

/** Map a period preset to a date range (computed client-side; `to` is open). */
export function rangeFor(period: TimesheetPeriod): TimesheetRange {
  const now = new Date();
  switch (period) {
    case 'this_week':
      return { from: startOfWeek(now, { weekStartsOn: 1 }).toISOString() };
    case 'this_month':
      return { from: startOfMonth(now).toISOString() };
    case 'this_quarter':
      return { from: startOfQuarter(now).toISOString() };
    case 'half_year':
      return { from: subMonths(now, 6).toISOString() };
    case 'this_year':
      return { from: startOfYear(now).toISOString() };
    case 'all':
      return {};
  }
}

const STATUS_TONE: Readonly<Record<SessionStatus, BadgeTone>> = {
  scheduled: 'violet',
  in_progress: 'amber',
  completed: 'green',
  cancelled: 'slate',
  no_show: 'slate',
  client_late: 'amber',
  consultant_attended_client_absent: 'rose',
};

function PeriodButton({
  period,
  active,
  onSelect,
}: {
  readonly period: TimesheetPeriod;
  readonly active: boolean;
  readonly onSelect: (p: TimesheetPeriod) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const select = useCallback(() => {
    onSelect(period);
  }, [onSelect, period]);
  return (
    <button
      type="button"
      onClick={select}
      className={cn(
        'focus-ring rounded-lg px-3 py-1.5 text-sm font-medium',
        active
          ? 'bg-[var(--accent)] text-white'
          : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300',
      )}
    >
      {t(`timesheet.period.${period}`)}
    </button>
  );
}

function SummaryCard({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string;
}): JSX.Element {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
      <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
      <p className="mt-1 text-xl font-bold text-slate-800 dark:text-white">{value}</p>
    </div>
  );
}

function TimesheetRowView({ row }: { readonly row: TimesheetRow }): JSX.Element {
  const { t } = useTranslation();
  return (
    <tr className="border-b border-slate-100 last:border-0 dark:border-slate-800">
      <td className="py-2 pr-3 text-sm">{formatIst(row.date, 'dd MMM yyyy')}</td>
      <td className="py-2 pr-3 text-sm">{row.clientName}</td>
      <td className="py-2 pr-3 text-sm text-slate-500">{row.sessionType ?? '—'}</td>
      <td className="py-2 pr-3 text-sm">
        {row.durationMin > 0 ? `${String(row.durationMin)}m` : '—'}
      </td>
      <td className="py-2 pr-3">
        <Badge label={t(`status.${row.status}`)} tone={STATUS_TONE[row.status]} />
      </td>
      <td className="py-2 pr-3 text-sm text-slate-500">{row.payPct}%</td>
      <td className="py-2 text-right text-sm font-medium">{formatInr(row.amountPaise)}</td>
    </tr>
  );
}

interface ViewProps {
  readonly data: Timesheet | undefined;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly period: TimesheetPeriod;
  readonly onPeriod: (p: TimesheetPeriod) => void;
}

/** Period selector + summary + session table (CR-002 / CR-004). */
export function TimesheetView({
  data,
  isLoading,
  isError,
  period,
  onPeriod,
}: ViewProps): JSX.Element {
  const { t } = useTranslation();
  const rows = data?.rows ?? [];
  const sessions = data?.totalSessions ?? 0;
  const hours = data?.totalHours ?? 0;
  const amountPaise = data?.totalAmountPaise ?? 0;
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2">
        {TIMESHEET_PERIODS.map((p) => (
          <PeriodButton key={p} period={p} active={p === period} onSelect={onPeriod} />
        ))}
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <SummaryCard label={t('timesheet.summary.sessions')} value={String(sessions)} />
        <SummaryCard label={t('timesheet.summary.hours')} value={String(hours)} />
        <SummaryCard label={t('timesheet.summary.earnings')} value={formatInr(amountPaise)} />
      </div>
      <QueryState
        isLoading={isLoading}
        isError={isError}
        isEmpty={rows.length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('timesheet.empty')}
      >
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs text-slate-400 dark:border-slate-700">
                <th className="pb-2 pr-3 font-medium">{t('timesheet.col.date')}</th>
                <th className="pb-2 pr-3 font-medium">{t('timesheet.col.client')}</th>
                <th className="pb-2 pr-3 font-medium">{t('timesheet.col.type')}</th>
                <th className="pb-2 pr-3 font-medium">{t('timesheet.col.duration')}</th>
                <th className="pb-2 pr-3 font-medium">{t('timesheet.col.status')}</th>
                <th className="pb-2 pr-3 font-medium">{t('timesheet.col.pay')}</th>
                <th className="pb-2 text-right font-medium">{t('timesheet.col.amount')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <TimesheetRowView key={row.sessionId} row={row} />
              ))}
            </tbody>
          </table>
        </div>
      </QueryState>
    </div>
  );
}
